import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X } from "lucide-react";
import { useDUDeclinedReasons } from "@/services/draftCalendar";

const STATUS_RESOLVED = "Resolved";
const STATUS_REMOVED = "Removed";
const STATUS_DU_DECLINED = "DU Declined";

const schema = z
  .object({
    status: z.string().min(1, "Please select Status."),
    duDeclineReason: z.string().optional(),
    duDeclineComments: z.string().optional(),
    comment: z.string().optional()
  })
  .superRefine((values, ctx) => {
    if (values.status === STATUS_REMOVED && !(values.comment ?? "").trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["comment"],
        message: "Comment is required for Removed status."
      });
    }

    if (values.status === STATUS_DU_DECLINED && !(values.duDeclineReason ?? "").trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["duDeclineReason"],
        message: "Please select DU decline reason."
      });
    }

    if (
      values.status === STATUS_DU_DECLINED &&
      (values.duDeclineReason ?? "").trim().toLowerCase() === "other" &&
      !(values.duDeclineComments ?? "").trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["duDeclineComments"],
        message: "DU decline comment is required when reason is Other."
      });
    }
  });

export type BulkChangeStatusValues = z.infer<typeof schema>;

interface BulkChangeStatusDialogProps {
  open: boolean;
  statuses: string[];
  onClose: () => void;
  onSubmit: (values: BulkChangeStatusValues) => void | Promise<void>;
  submitting?: boolean;
}

export function BulkChangeStatusDialog({
  open,
  statuses,
  onClose,
  onSubmit,
  submitting = false
}: BulkChangeStatusDialogProps) {
  const { data: declineReasons = [] } = useDUDeclinedReasons(open);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const form = useForm<BulkChangeStatusValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      status: "",
      duDeclineReason: "",
      duDeclineComments: "",
      comment: ""
    }
  });

  const selectedStatus = form.watch("status");
  const selectedReason = form.watch("duDeclineReason");

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      form.reset({
        status: "",
        duDeclineReason: "",
        duDeclineComments: "",
        comment: ""
      });

      requestAnimationFrame(() => {
        closeButtonRef.current?.focus();
      });

      return;
    }

    previousFocusRef.current?.focus();
  }, [open, form]);

  const statusOptions = useMemo(() => statuses.filter(Boolean), [statuses]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      tabIndex={-1}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-status-title"
        className="w-full max-w-xl rounded border border-slate-300 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 id="bulk-status-title" className="text-base font-semibold text-slate-900">
            Change Status
          </h2>
          <button type="button" aria-label="Close" onClick={onClose} ref={closeButtonRef} className="text-slate-500 hover:text-slate-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form
          onSubmit={form.handleSubmit(async (values) => {
            await onSubmit(values);
          })}
          className="space-y-4 px-4 py-4"
        >
          <div>
            <label htmlFor="bulk-status" className="mb-1 block text-sm font-medium text-slate-700">
              Change Status to <span className="text-red-600">*</span>
            </label>
            <select
              id="bulk-status"
              {...form.register("status")}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">--</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            {form.formState.errors.status && (
              <p className="mt-1 text-xs text-red-600">{form.formState.errors.status.message}</p>
            )}
          </div>

          {selectedStatus === STATUS_DU_DECLINED && (
            <>
              <div>
                <label htmlFor="du-decline-reason" className="mb-1 block text-sm font-medium text-slate-700">
                  Reason for DU Decline <span className="text-red-600">*</span>
                </label>
                <select
                  id="du-decline-reason"
                  {...form.register("duDeclineReason")}
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="">--</option>
                  {declineReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
                {form.formState.errors.duDeclineReason && (
                  <p className="mt-1 text-xs text-red-600">{form.formState.errors.duDeclineReason.message}</p>
                )}
              </div>

              {(selectedReason ?? "").trim().toLowerCase() === "other" && (
                <div>
                  <label htmlFor="du-decline-comments" className="mb-1 block text-sm font-medium text-slate-700">
                    DU Decline Comment <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="du-decline-comments"
                    type="text"
                    {...form.register("duDeclineComments")}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  />
                  {form.formState.errors.duDeclineComments && (
                    <p className="mt-1 text-xs text-red-600">{form.formState.errors.duDeclineComments.message}</p>
                  )}
                </div>
              )}
            </>
          )}

          <div>
            <label htmlFor="bulk-comment" className="mb-1 block text-sm font-medium text-slate-700">
              Add Comment{selectedStatus === STATUS_REMOVED ? <span className="text-red-600"> *</span> : null}
            </label>
            <textarea
              id="bulk-comment"
              rows={4}
              {...form.register("comment")}
              placeholder="Add a comment..."
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            />
            {form.formState.errors.comment && (
              <p className="mt-1 text-xs text-red-600">{form.formState.errors.comment.message}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {selectedStatus === STATUS_RESOLVED ? "Continue" : "Update"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export const BULK_STATUS_CONSTANTS = {
  STATUS_RESOLVED,
  STATUS_REMOVED,
  STATUS_DU_DECLINED
};
